import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard,
  formatPortalDateTime,
  getAccessStatusMeta,
  usePortalTableStyles
} from './portalShared'

const useStyles = makeStyles((theme) => ({
  roleChip: {
    backgroundColor: 'var(--df-info-bg)',
    color: 'var(--df-info-text)',
    border: '1px solid var(--df-info-text)',
    fontWeight: 700
  },
  emptyCell: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  }
}))

const SubcontractorUsers = ({ user, notification }) => {
  const classes = useStyles()
  const shared = usePortalTableStyles()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [summary, setSummary] = useState({})
  const [offboardingId, setOffboardingId] = useState('')
  const isAdmin = user?.subcontractorProfile?.role === 'admin'
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [crews, setCrews] = useState([])
  const [inviteForm, setInviteForm] = useState({ firstName: '', lastName: '', phone: '', email: '', role: 'applicator', crewId: '' })
  const [inviteError, setInviteError] = useState('')
  const [resendingId, setResendingId] = useState('')

  const loadUsers = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await axios.get('/api/subcontractor/active-users')
      setItems(response.data?.items || [])
      setSummary(response.data?.summary || {})
    } catch (requestError) {
      console.error('Failed to load subcontractor users', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load subcontractor users right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const loadCrews = useCallback(async () => {
    if (!isAdmin) return
    try {
      const res = await axios.get('/api/subcontractor/crews')
      setCrews((res.data?.items || []).filter(c => c.isActive !== false))
    } catch (err) {
      console.error('Failed to load crews for invite', err)
    }
  }, [isAdmin])

  useEffect(() => { loadCrews() }, [loadCrews])

  const counts = useMemo(() => ({
    admins: items.filter(item => item.role === 'admin').length,
    applicators: items.filter(item => item.role !== 'admin').length,
    invited: items.filter(item => item.accessStatus === 'invited').length
  }), [items])

  const handleOffboard = async (employeeId, displayName) => {
    const confirmed = window.confirm(`Offboard ${displayName}? This will immediately block future portal access.`)
    if (!confirmed) {
      return
    }

    setOffboardingId(employeeId)

    try {
      await axios.post(`/api/subcontractor/users/${employeeId}/offboard`)
      notification?.({
        type: 'success',
        title: 'Access Revoked',
        message: `${displayName} has been offboarded.`
      })
      await loadUsers()
    } catch (requestError) {
      console.error('Failed to offboard subcontractor user', requestError)
      notification?.({
        type: 'danger',
        title: 'Offboarding Failed',
        message: requestError?.response?.data?.message || 'Unable to revoke access for that user.'
      })
    } finally {
      setOffboardingId('')
    }
  }

  const handleInvite = async () => {
    if (!inviteForm.firstName.trim() || !inviteForm.lastName.trim() || !inviteForm.phone.trim()) {
      setInviteError('First name, last name, and phone are required.')
      return
    }
    setInviting(true)
    setInviteError('')
    try {
      await axios.post('/api/subcontractor/invite', {
        firstName: inviteForm.firstName.trim(),
        lastName: inviteForm.lastName.trim(),
        phone: inviteForm.phone.trim(),
        email: inviteForm.email.trim() || undefined,
        role: inviteForm.role,
        crewId: inviteForm.crewId || undefined
      })
      notification?.({
        type: 'success',
        title: 'Invite Sent',
        message: inviteForm.email.trim()
          ? `Invite sent to ${inviteForm.firstName.trim()} by SMS${inviteForm.email.trim() ? ' and email when enabled' : ''}.`
          : `SMS invite sent to ${inviteForm.firstName.trim()}.`
      })
      setInviteOpen(false)
      setInviteForm({ firstName: '', lastName: '', phone: '', email: '', role: 'applicator', crewId: '' })
      await loadUsers()
    } catch (err) {
      console.error('Failed to invite user', err)
      if (err?.response?.status === 409) {
        const existing = err.response.data?.employee
        setInviteError(`A user with that phone number already exists${existing?.firstName ? ` (${existing.firstName} ${existing.lastName || ''})` : ''}.`)
      } else {
        setInviteError(err?.response?.data?.message || 'Unable to send invite right now.')
      }
    } finally {
      setInviting(false)
    }
  }

  const handleResend = async (employeeId, displayName) => {
    setResendingId(employeeId)
    try {
      await axios.post('/api/subcontractor/resend-invite', { employeeId })
      notification?.({ type: 'success', title: 'Invite Resent', message: `SMS re-sent to ${displayName}.` })
    } catch (err) {
      console.error('Failed to resend invite', err)
      notification?.({ type: 'danger', title: 'Resend Failed', message: err?.response?.data?.message || 'Unable to resend invite.' })
    } finally {
      setResendingId('')
    }
  }

  const summaryCards = [
    {
      eyebrow: 'Visible Users',
      value: summary.total || 0,
      caption: 'Total team members in your organization.'
    },
    {
      eyebrow: 'Active Accounts',
      value: summary.active || 0,
      caption: 'Members with active login access.'
    },
    {
      eyebrow: 'Admins',
      value: counts.admins,
      caption: 'Members who can manage the portal and team.'
    },
    {
      eyebrow: 'Applicators',
      value: counts.applicators,
      caption: 'Field members assigned to specific crews.'
    }
  ]

  if (loading && !items.length) {
    return <PortalLoadingCard title="Loading users" message="Loading your team..." minHeight={280} />
  }

  return (
    <div>
      <Grid container spacing={3}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.eyebrow}>
            <PortalMetricCard
              eyebrow={card.eyebrow}
              value={card.value}
              caption={card.caption}
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={3}>
        <Card className={shared.controlsCard}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gridGap={12}>
              <div>
                <Typography variant="h6" className={shared.cardTitle}>
                  Team Management
                </Typography>
                <Typography variant="body2" className={shared.helperText}>
                  {isAdmin
                    ? 'As an admin, you can view everyone on your team and manage their access.'
                    : 'You can see who is on your team, but only admins can manage access.'}
                </Typography>
              </div>
              <Box display="flex" gridGap={8} flexWrap="wrap">
                <Chip label={`Invited ${counts.invited}`} className="df-chip-info" />
                <Chip label={isAdmin ? 'User management enabled' : 'Read-only access'} className={isAdmin ? 'df-chip-ok' : ''} />
                {loading ? <Chip label="Refreshing..." className="df-chip-info" /> : null}
                {isAdmin ? (
                  <Button color="primary" variant="contained" size="small" onClick={() => { setInviteOpen(true); loadCrews() }}>
                    + Invite User
                  </Button>
                ) : null}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {error ? (
        <PortalStateCard
          title="Users unavailable"
          message={error}
          actionLabel="Retry"
          onAction={loadUsers}
          tone="danger"
          minHeight={220}
        />
      ) : (
        <Card className={shared.tableCard}>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={shared.tableHeadCell}>User</TableCell>
                    <TableCell className={shared.tableHeadCell}>Role</TableCell>
                    <TableCell className={shared.tableHeadCell}>Crew</TableCell>
                    <TableCell className={shared.tableHeadCell}>Portal Status</TableCell>
                    <TableCell className={shared.tableHeadCell}>Last Mobile Activity</TableCell>
                    {isAdmin ? <TableCell className={shared.tableHeadCell} align="right">Actions</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    const accessStatus = getAccessStatusMeta(item.accessStatus)

                    return (
                      <TableRow key={item._id} hover>
                        <TableCell>
                          <Typography variant="body2" className={shared.primaryText}>
                            {item.displayName}
                          </Typography>
                          <Typography variant="body2" className={shared.secondaryText}>
                            {item.email || 'No email on file'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={item.role === 'admin' ? 'Admin' : 'Applicator'}
                            className={classes.roleChip}
                          />
                        </TableCell>
                        <TableCell>{item.crewName || 'Unassigned'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={accessStatus.label} className={accessStatus.className} />
                        </TableCell>
                        <TableCell>
                          {formatPortalDateTime(item.lastMobileActivity, {
                            fallback: item.active ? 'No recent mobile activity' : 'User inactive'
                          })}
                        </TableCell>
                        {isAdmin ? (
                          <TableCell align="right">
                            {item.active ? (
                              <Button
                                size="small"
                                className="df-danger"
                                disabled={offboardingId === item._id}
                                onClick={() => handleOffboard(item._id, item.displayName)}
                              >
                                {offboardingId === item._id ? 'Offboarding...' : 'Offboard'}
                              </Button>
                            ) : (
                              <Typography variant="caption" className={shared.secondaryText}>
                                No action available
                              </Typography>
                            )}
                            {item.accessStatus === 'invited' ? (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={resendingId === item._id}
                                onClick={() => handleResend(item._id, item.displayName)}
                              >
                                {resendingId === item._id ? 'Sending...' : 'Resend'}
                              </Button>
                            ) : null}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    )
                  })}
                  {!items.length ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 6 : 5} className={classes.emptyCell}>
                        <PortalStateCard
                          title="No users in scope"
                          message="No team members to show yet."
                          minHeight={180}
                        />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog open={inviteOpen} onClose={() => !inviting && setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="First Name" variant="outlined" value={inviteForm.firstName} onChange={(e) => setInviteForm(f => ({ ...f, firstName: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Last Name" variant="outlined" value={inviteForm.lastName} onChange={(e) => setInviteForm(f => ({ ...f, lastName: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Phone Number" variant="outlined" value={inviteForm.phone} onChange={(e) => setInviteForm(f => ({ ...f, phone: e.target.value }))} helperText="An SMS invite will be sent to this number." />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email (Optional)" variant="outlined" value={inviteForm.email} onChange={(e) => setInviteForm(f => ({ ...f, email: e.target.value }))} helperText="If invite email delivery is enabled, a copy can be sent here too." />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Role" variant="outlined" value={inviteForm.role} onChange={(e) => setInviteForm(f => ({ ...f, role: e.target.value }))}>
                <MenuItem value="applicator">Applicator</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Crew" variant="outlined" value={inviteForm.crewId} onChange={(e) => setInviteForm(f => ({ ...f, crewId: e.target.value }))}>
                <MenuItem value="">No crew assignment</MenuItem>
                {crews.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            {inviteError ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">{inviteError}</Typography>
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={inviting}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={handleInvite} disabled={inviting}>
            {inviting ? 'Sending...' : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorUsers)
